import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { statusColors, statusLabels } from "@/features/purchase-invoices";
import { apiOutletClient, formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import { SupplierSelect } from "@/features/suppliers";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const PurchaseDownPayments = () => {
  const { outlet, changeOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseDownPayments, setPurchaseDownPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  const seller = useMemo(() => {
    const sellerParam = searchParams.get("seller");
    if (!sellerParam) return null;
    try {
      return JSON.parse(sellerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadPurchaseDownPayments = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("purchase-down-payments", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          sellerableId: seller?.id || null,
          withSeller: true,
          withCreator: true,
        },
      });
      setPurchaseDownPayments(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat uang muka pembelian"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    seller,
    showSnackbar,
  ]);

  useEffect(() => {
    loadPurchaseDownPayments();
  }, [loadPurchaseDownPayments]);

  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("seller");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleSellerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "seller",
          JSON.stringify({ id: value.id, name: value.name }),
        );
      } else {
        newParams.delete("seller");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_PURCHASE_DOWN_PAYMENTS,
  ) && (
    <Button variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <SupplierSelect
                value={seller}
                onChange={handleSellerChange}
                TextFieldProps={{
                  label: "Pemasok (Supplier)",
                }}
              />
            ) : (
              <AllOutletsSelect
                value={seller}
                onChange={handleSellerChange}
                allowedRoles={[OutletRoleEnum.HUB]}
                TextFieldProps={{
                  label: "Pemasok (Hub)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Pembeli",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchaseDownPayments}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => {
                const today = dayjs().startOf("day");
                const transaction = dayjs(transactionDate).startOf("day");
                const age = today.diff(transaction, "day");
                return age > 0
                  ? `${formatDate(transactionDate)} (${age}d)`
                  : formatDate(transactionDate);
              },
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "seller",
              headerName: "Pemasok",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value ?? "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "grandTotal",
              headerName: "Total",
              flex: 1,
              sortable: false,
              type: "number",
              valueGetter: (value, row) => formatNumber(row.grandTotal),
            },
            ...(hasPermission(PermissionEnum.READ_PURCHASE_DOWN_PAYMENTS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="view"
                        onClick={() =>
                          navigate(`${params.row.referenceNumber}`)
                        }
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
